import { FC } from "react";
import { t } from "i18next";
import { FaCheck, FaTimes } from "react-icons/fa";
import { FcLock } from "react-icons/fc";

import { Stepper } from "@components/shared/stepper/Stepper";
import { AMENITY_CONSTANTS } from "@constants/amenities";
import { AmenityCardProps } from "@models/components/amenity.model";
import { formatPrice } from "@utils/common.utils";

export const AmenityCard: FC<AmenityCardProps> = props => {
  const {
    currency,
    description,
    id,
    isBooked = false,
    isBookingFinanceConfigured = true,
    isDisabled = false,
    isMandatory = false,
    isSelected = false,
    isStepperDisabled = false,
    isVatInclusive,
    maxQuantity = AMENITY_CONSTANTS.DEFAULT_COUNT,
    minQuantity,
    onAdd,
    onCheckNextAvailability,
    onInfo,
    onQuantityChange,
    onRemove,
    price,
    quantity,
    showAddMore = false,
    title,
    uuid,
    vatPercentage,
  } = props;
  const formattedPrice = formatPrice(price, currency);
  const vatText = isVatInclusive
    ? t("inclVat", { rate: vatPercentage })
    : t("exclVat", { rate: vatPercentage });

  const handleInfo = () => onInfo(id);

  const handleAdd = () => onAdd(id);

  const handleRemove = () => onRemove(id, isBooked, uuid);

  const checkAvailabilityBtnId = `fg-btn-check-availability-${id}`;

  const handleCheckNextAvailability = (
    e?: React.MouseEvent<HTMLButtonElement>
  ) =>
    onCheckNextAvailability?.(
      id,
      title,
      e?.currentTarget?.id || e?.currentTarget || checkAvailabilityBtnId
    );

  const handleIncrement = () =>
    onQuantityChange(
      id,
      quantity + AMENITY_CONSTANTS.PER_AMENITY,
      isBooked,
      uuid
    );
  const handleDecrement = () =>
    onQuantityChange(
      id,
      quantity - AMENITY_CONSTANTS.PER_AMENITY,
      isBooked,
      uuid
    );

  const isCheckAvailabilityShowing = Boolean(
    onCheckNextAvailability &&
      !isBooked &&
      (isDisabled ||
        (isSelected && props.error) ||
        props.amenityAddedUnavailableErrorIds?.includes(props.id))
  );

  const isUnavailable = Boolean(
    !isBooked &&
      (isCheckAvailabilityShowing ||
        isDisabled ||
        Boolean(props.error) ||
        props.amenityAddedUnavailableErrorIds?.includes(props.id))
  );

  const renderCheckAvailabilityButton = () => (
    <button
      id={checkAvailabilityBtnId}
      className="fg-btn-check-availability"
      onClick={handleCheckNextAvailability}
      type="button"
    >
      {t("checkNextAvailability")}
    </button>
  );

  const renderRemoveButton = () => (
    <button className="fg-btn-remove" onClick={handleRemove} type="button">
      <FaTimes className="fg-mr-1" /> {t("remove")}
    </button>
  );

  const renderCheckAvailabilityAndRemove = () => (
    <div className="d-flex align-items-center gap-2">
      {renderCheckAvailabilityButton()}
      {renderRemoveButton()}
    </div>
  );

  const renderFooterAction = () => {
    if (
      isSelected &&
      isDisabled &&
      !isBooked &&
      isMandatory &&
      props.amenityAddedUnavailableErrorIds?.includes(props.id)
    ) {
      return onCheckNextAvailability
        ? renderCheckAvailabilityAndRemove()
        : renderRemoveButton();
    }
    if (!isSelected && isDisabled) {
      return onCheckNextAvailability ? (
        renderCheckAvailabilityButton()
      ) : (
        <span className="fg-included-badge disabled">
          {t("bookingEdit.amenityUnavailableWarningShort")}
        </span>
      );
    }
    if (isMandatory && !isBooked && !props.error) {
      if (isDisabled) {
        return onCheckNextAvailability ? (
          renderCheckAvailabilityButton()
        ) : (
          <span className="fg-included-badge disabled">
            {t("bookingEdit.amenityUnavailableWarningShort")}
          </span>
        );
      }
      return (
        <span className="fg-included-badge">
          {!isDisabled && <FaCheck className="fg-check-icon" />}
          {t("included")}
        </span>
      );
    }
    if (isBooked || isSelected) {
      if (
        onCheckNextAvailability &&
        (props.error ||
          isDisabled ||
          props.amenityAddedUnavailableErrorIds?.includes(props.id)) &&
        !isBooked
      ) {
        return renderCheckAvailabilityAndRemove();
      }
      return renderRemoveButton();
    }
    return (
      <button
        className="fg-btn-add"
        onClick={handleAdd}
        disabled={
          isDisabled || isStepperDisabled || props?.amenityPriceChangedError
        }
      >
        + {showAddMore ? t("addMore") : t("add")}
      </button>
    );
  };

  return (
    <div
      className={`fg-amenity-card d-flex ${
        isUnavailable ? "unavailable" : ""
      } ${
        isDisabled && isMandatory && !isSelected && !isBooked
          ? "disabled"
          : `${isSelected ? "selected" : ""} ${isMandatory ? "mandatory" : ""}`
      } ${isBooked ? "original" : ""}`.trim()}
    >
      <div className="fg-amenity-card-left pt-1 pe-3">
        {(!!description?.trim() ||
          (price !== undefined && price !== null && price !== 0)) && (
          <button
            type="button"
            className="fg-info-btn d-flex align-items-center justify-content-center"
            onClick={handleInfo}
            aria-label={t("info")}
          >
            i
          </button>
        )}
      </div>

      <div className="fg-amenity-card-right flex-grow-1 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start">
          <h5 className="fg-amenity-title m-0">{title as string}</h5>
          <div className="fg-amenity-icons d-flex align-items-center gap-1">
            {isCheckAvailabilityShowing && (
              <span className="fg-amenity-unavailable-badge badge bg-danger rounded-pill">
                {props.error || t("bookingEdit.amenityUnavailableWarningShort")}
              </span>
            )}
            {isMandatory && (
              <FcLock
                className="fg-lock-icon"
                data-tooltip-id="amenity-mandatory-tooltip"
                data-tooltip-content={t("mandatory")}
              />
            )}
            {isSelected && !isMandatory && !isBooked && (
              <FaCheck className="fg-selected-icon ms-2" />
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          {isBookingFinanceConfigured && (
            <p className="fg-amenity-price m-0">{formattedPrice}</p>
          )}
          {isSelected &&
            !isDisabled &&
            ((!isMandatory &&
              maxQuantity > AMENITY_CONSTANTS.DEFAULT_AMENITY_COUNT) ||
              (isMandatory && maxQuantity > 1)) &&
            maxQuantity > 1 && (
              <div className="fg-card-stepper ms-auto">
                {isBooked ? (
                  <div className="fg-qty-static d-inline-flex align-items-center justify-content-center gap-1">
                    {quantity} <span className="fg-qty-label">{t("qty")}</span>
                  </div>
                ) : (
                  <Stepper
                    quantity={quantity}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    disableDecrement={
                      quantity <=
                      (minQuantity ??
                        (isMandatory
                          ? AMENITY_CONSTANTS.DEFAULT_AMENITY_COUNT
                          : AMENITY_CONSTANTS.DEFAULT_COUNT))
                    }
                    disableIncrement={
                      (maxQuantity > AMENITY_CONSTANTS.DEFAULT_AMENITY_COUNT ||
                        maxQuantity === 0) &&
                      quantity >= maxQuantity
                    }
                    isStepperDisabled={
                      isStepperDisabled || props?.amenityPriceChangedError
                    }
                  />
                )}
              </div>
            )}
        </div>

        <div className="d-flex justify-content-between align-items-end mt-auto pt-2">
          {!isCheckAvailabilityShowing && (
            <div className="fg-amenity-details text-secondary m-0">
              <div>
                {t("max")}: {maxQuantity}
              </div>
              {vatPercentage > AMENITY_CONSTANTS.DEFAULT_VAT_VALUE && (
                <div className="mt-1">{vatText}</div>
              )}
            </div>
          )}
          <div className="fg-amenity-action ms-auto">
            {renderFooterAction()}
          </div>
        </div>
      </div>
    </div>
  );
};
